import { gql, useQuery } from '@apollo/client';
import {
  Button,
  ButtonGroup,
  CardBody,
  CardDescription,
  CardHeader,
  CardTitle,
  Link,
} from '@apollo/orbit';
import moment from 'moment';
import React, { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';

import { useGraphRef } from 'src/app/graph/hooks/useGraphRef';
import { launchesRouteConfig } from 'src/app/graph/routes';
import { LaunchErrorModal } from 'src/app/graph/variantLayout/launchErrorModal/LaunchErrorModal';
import { Loading } from 'src/components/common/loading/Loading';
import { NotFound } from 'src/components/common/notFound/NotFound';
import { URLModal } from 'src/components/common/URLModal';
import { ignorePermissionsErrors } from 'src/lib/apollo/catchErrors';
import { appLinkContext } from 'src/lib/apollo/link';
import { assertUnreachable } from 'src/lib/assertUnreachable';
import Config from 'src/lib/config';
import { date } from 'src/lib/date/date';
import { GraphQLTypes } from 'src/lib/graphqlTypes';
import { setOverlayVisibleLocation } from 'src/lib/routing';

import { LaunchIDHash } from '../../LaunchIDHash';
import { LaunchSupergraphDiffViewer } from '../../launchSupergraphDiffViewer/LaunchSupergraphDiffViewer';
import { LaunchUpdatedSubgraphs } from '../../LaunchUpdatedSubgraphs';
import { getLaunchStatusText } from '../launchHistoryList/launchHistoryListItem/LaunchHistoryListItem';

import {
  LaunchDetailsCard,
  LaunchDetailsCardFragment,
} from './launchInfo/LaunchDetailsCard/LaunchDetailsCard';
import {
  LaunchSequence,
  LaunchSequenceLaunchFragment,
} from './launchSequence/LaunchSequence';
import { BuildInputsModal } from './modals/BuildInputsModal';
import { FetchAndViewSchemaModal } from './modals/FetchAndViewSchemaModal';

interface Props {
  // Note that the launch ID must be in the launch history.
  launchId: string;
  launchHistory: GraphQLTypes.LaunchHistorySchemaDiffFragment[];
  isCloudSupergraph: Boolean;
}

export const LaunchDetailsQuery = gql<
  GraphQLTypes.LaunchDetailsQuery,
  GraphQLTypes.LaunchDetailsQueryVariables
>`
  query LaunchDetailsQuery(
    $graphId: ID!
    $graphVariant: String!
    $launchId: ID!
    $skipOrder: Boolean!
  ) {
    service(id: $graphId) {
      id
      title
      variant(name: $graphVariant) {
        id
        sourceVariant {
          id
          name
        }
        launch(id: $launchId) {
          id
          createdAt
          completedAt
          status
          shortenedID
          build {
            id
            result {
              __typename
              ... on BuildSuccess {
                coreSchema {
                  coreHash
                  coreDocument
                }
              }
            }
          }
          isPublished
          subgraphChanges {
            name
            type
          }
          ...LaunchDetailsCardFragment
          ...LaunchSequenceLaunchFragment
        }
      }
    }
  }
  ${LaunchDetailsCardFragment}
  ${LaunchSequenceLaunchFragment}
`;

export const LaunchHistorySchemaDiffFragment = gql`
  fragment LaunchHistorySchemaDiffFragment on Launch {
    id
    shortenedID
    createdAt
    build {
      id
      result {
        __typename
      }
    }
  }
`;

export const LaunchDetails = ({
  launchId,
  launchHistory,
  isCloudSupergraph,
}: Props) => {
  const [modalVisible, setModalVisible] = useState<
    'SupergraphSchema' | 'BuildInput' | 'BuildError' | null
  >(null);

  const location = useLocation();
  const graphRef = useGraphRef();
  const prevSuccessfulResult = useRef(
    new Map<string, GraphQLTypes.LaunchDetailsQuery>(),
  );
  if (!graphRef) {
    throw new Error('expected a graph ref');
  }

  const { data, loading } = useQuery<
    GraphQLTypes.LaunchDetailsQuery,
    GraphQLTypes.LaunchDetailsQueryVariables
  >(LaunchDetailsQuery, {
    variables: {
      graphId: graphRef.graphId,
      graphVariant: graphRef.graphVariant,
      launchId,
      skipOrder: !isCloudSupergraph,
    },
    pollInterval: 5000,
    context: appLinkContext({ catchErrors: [ignorePermissionsErrors] }),
  });

  useEffect(() => {
    if (data) prevSuccessfulResult.current.set(launchId, data);
  }, [launchId, data]);

  // This is a polling query so show spinner and retry until data is returned
  // then ignore errors and use saved state
  if (loading || (!prevSuccessfulResult.current.get(launchId) && !data)) {
    return <Loading />;
  }

  const result = data || prevSuccessfulResult.current.get(launchId);

  if (!result?.service?.variant?.launch) {
    return <NotFound />;
  }

  const { launch } = result.service.variant;
  const { build, subgraphChanges } = launch;

  const createdTime = moment(launch.createdAt);
  const endedTime = launch.completedAt ? moment(launch.completedAt) : moment();

  const supergraphSdl =
    build?.result?.__typename === 'BuildSuccess'
      ? build.result.coreSchema.coreDocument
      : null;
  const supergraphDiffLaunches = launchHistory?.filter(
    (diffLaunch) => diffLaunch.build?.result?.__typename === 'BuildSuccess',
  );
  const allowSupergraphDiffViewer =
    supergraphDiffLaunches?.some((diffLaunch) => diffLaunch.id === launchId) &&
    supergraphDiffLaunches.length >= 2;

  return (
    <>
      <CardHeader className="border-b border-b-primary">
        <CardTitle>
          {getLaunchStatusText({
            status: launch.status,
            superseded: Boolean(launch.supersededBy?.id),
          })}
          <LaunchIDHash
            launchID={launch.id}
            shortLaunchID={launch.shortenedID}
          />
        </CardTitle>
        <CardDescription className="overflow-hidden truncate">
          <span>
            {launch.status === GraphQLTypes.LaunchStatus.LAUNCH_INITIATED
              ? createdTime.format('MMM D, YYYY [at] h:mm A')
              : date.printDateRange(createdTime, endedTime)}
          </span>
          <span className="mx-2">&bull;</span>
          <span className="text-secondary">
            {date.printRangeDuration(
              moment.duration(endedTime.diff(createdTime)),
            )}
          </span>
        </CardDescription>

        <div className="inline-flex flex-wrap items-center">
          {subgraphChanges && subgraphChanges.length > 0 && (
            <LaunchUpdatedSubgraphs subgraphChanges={subgraphChanges} />
          )}
          {subgraphChanges &&
            subgraphChanges.length > 0 &&
            allowSupergraphDiffViewer &&
            supergraphSdl && (
              <span className="mr-2 text-sm text-secondary">•</span>
            )}
          {allowSupergraphDiffViewer && supergraphSdl && (
            <Link
              className="truncate text-sm"
              to={setOverlayVisibleLocation({
                location,
                overlayName: Config.modals.supergraphDiffViewer,
              })}
            >
              Supergraph diff
            </Link>
          )}
          {launch?.upstreamLaunch !== null && (
            <>
              <span className="mx-2 text-sm text-secondary">•</span>
              <Link
                className="truncate text-sm"
                to={launchesRouteConfig.location({
                  graphId: launch.upstreamLaunch.graphId,
                  graphVariant: launch.upstreamLaunch.graphVariant,
                  launchId: launch.upstreamLaunch.id,
                })}
              >
                Source launch
              </Link>
            </>
          )}
        </div>

        <ButtonGroup>
          <Button
            variant="secondary"
            onClick={() => setModalVisible('BuildInput')}
          >
            Build inputs
          </Button>
          {build?.result?.__typename === 'BuildFailure' ? (
            <Button onClick={() => setModalVisible('BuildError')}>
              View build errors
            </Button>
          ) : (
            <Button
              variant="secondary"
              onClick={() => setModalVisible('SupergraphSchema')}
            >
              Supergraph schema
            </Button>
          )}
        </ButtonGroup>
      </CardHeader>
      <CardBody className="flex-row">
        <div className="min-w-0 flex-3">
          <LaunchSequence launch={launch} />
        </div>
        <div className="min-w-0 flex-2">
          <LaunchDetailsCard
            launch={launch}
            isCloudSupergraph={isCloudSupergraph}
          />
        </div>
      </CardBody>
      {allowSupergraphDiffViewer && supergraphSdl && (
        <URLModal name={Config.modals.supergraphDiffViewer}>
          {({ isOpen }) => (
            // Note we intentionally do not pass closeModal() here; see the
            // comments in LaunchSupergraphDiffViewer for details.
            <LaunchSupergraphDiffViewer
              isOpen={isOpen}
              launches={supergraphDiffLaunches ?? []}
              baseLaunchId={launchId}
              baseLaunchSchema={supergraphSdl}
            />
          )}
        </URLModal>
      )}
      {modalVisible === 'SupergraphSchema' ? (
        <FetchAndViewSchemaModal
          graphId={graphRef.graphId}
          graphVariant={graphRef.graphVariant}
          launchId={launchId}
          onClose={() => {
            setModalVisible(null);
          }}
        />
      ) : modalVisible === 'BuildInput' ? (
        <BuildInputsModal
          isOpen
          graphId={graphRef.graphId}
          graphVariant={graphRef.graphVariant}
          launchId={launchId}
          onClose={() => {
            setModalVisible(null);
          }}
        />
      ) : modalVisible === 'BuildError' ? (
        <LaunchErrorModal
          isOpen
          graphId={graphRef.graphId}
          graphVariant={graphRef.graphVariant}
          launchId={launchId}
          onClose={() => {
            setModalVisible(null);
          }}
        />
      ) : modalVisible === null ? null : (
        assertUnreachable(modalVisible)
      )}
    </>
  );
};
